import React from "react";
import {
    type BrowserPlugin,
    addGlobalContexts,
    newTracker,
    enableActivityTracking,
    trackPageView as snowplowTrackPageView,
    removeGlobalContexts,
    trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";
import {
    LinkClickTrackingPlugin,
    trackLinkClick as snowplowTrackLinkClick,
} from "@snowplow/browser-plugin-link-click-tracking";

const defaultPagePingConfiguration = {
    minimumVisitLength: 5,
    heartbeatDelay: 20,
};

const tabs = ["news", "community", "esport"] as const;
type TabType = (typeof tabs)[number];
type Page = {
    id: string;
    locale: string;
    tab: TabType;
};
type PageContext = Page & {
    maintenance: boolean;
};
type PageView = {
    title: string;
    page: Page;
};

// Snowplow reads the page URL, referrer and device timestamp when an event is
// tracked. Queued events are tracked later, so take them when the event happens.
type PageSnapshot = { url: string; referrer: string; timestamp: number };

// Mirrors the tracker's single-page-app referrer: the URL the previous event
// saw, or document.referrer for the first one.
let lastSeenUrl: string | undefined;
let lastReferrer = "";
const snapshotPage = (): PageSnapshot => {
    const url = window.location.href;
    if (lastSeenUrl === undefined) {
        lastReferrer = document.referrer;
    } else if (url !== lastSeenUrl) {
        lastReferrer = lastSeenUrl;
    }
    lastSeenUrl = url;
    return { url, referrer: lastReferrer, timestamp: Date.now() };
};

// Set while a queued event is tracked so its payload gets the page state from
// when it happened, not from when the queue was flushed. Configured plugins run
// after the tracker's built-in one that fills in url/refr, so these win.
let replaying: PageSnapshot | null = null;
const QueuedEventPagePlugin = (): BrowserPlugin => ({
    beforeTrack: (payloadBuilder) => {
        if (!replaying) return;
        payloadBuilder.add("url", replaying.url);
        payloadBuilder.add("dtm", String(replaying.timestamp));
        if (replaying.referrer) {
            payloadBuilder.add("refr", replaying.referrer);
        } else {
            // add() ignores empty strings
            delete payloadBuilder.getPayload().refr;
        }
    },
});

const trackAsOf = (snapshot: PageSnapshot, track: () => void) => {
    replaying = snapshot;
    try {
        track();
    } finally {
        replaying = null;
    }
};

function init(appId: string, collectorUrl: string) {
    newTracker("sp1", collectorUrl, {
        appId,
        plugins: [LinkClickTrackingPlugin(), QueuedEventPagePlugin()],
    });
    enableActivityTracking(defaultPagePingConfiguration);
}

const pageContextWithSchema = (context: PageContext) => ({
    schema: "iglu:com.supercell/inbox_page/jsonschema/1-0-1",
    data: context,
});

let lastPageContext: PageContext | null = null;
const addGlobalPageContext = (context: PageContext) => {
    if (lastPageContext) {
        removeGlobalContexts([pageContextWithSchema(lastPageContext)]);
    }
    addGlobalContexts([pageContextWithSchema(context)]);
    lastPageContext = context;
};

const addContextAndTrackPageView = (title: string, context: PageContext) => {
    addGlobalPageContext(context);
    snowplowTrackPageView({
        title,
    });
};

const trackLinkClick = ({
    url,
    id,
    label,
    additionalInformation,
}: TrackLinkClickParams) => {
    snowplowTrackLinkClick({
        targetUrl: url,
        elementId: id,
        elementContent: label,
        ...(additionalInformation
            ? {
                  context: [
                      {
                          schema: "iglu:com.snowplowanalytics.snowplow/additional_information/jsonschema/1-0-0",
                          data: additionalInformation,
                      },
                  ],
              }
            : {}),
    });
};
const trackButtonClick = (
    id: string,
    label?: string,
    additionalInformation?: Record<string, unknown>,
) => {
    trackSelfDescribingEvent({
        event: {
            schema: "iglu:com.supercell/button_click/jsonschema/1-0-1",
            data: {
                button_id: id,
                // button_click (version 1-0-1) schema has a 40 character limit on button_name
                button_name: label?.substring(0, 40) ?? "",
            },
        },
        ...(additionalInformation
            ? {
                  context: [
                      {
                          schema: "iglu:com.snowplowanalytics.snowplow/additional_information/jsonschema/1-0-0",
                          data: additionalInformation,
                      },
                  ],
              }
            : {}),
    });
};

const localeAndTab = /\/(?<locale>\w+)\/(?<tab>\w+)/;
const articleId = /\/(?<locale>\w+)\/(?<tab>\w+)\/(?<articleId>[\w_-]+)\//;
const getPageFromPath = (path: string) => {
    const localeAndTabResult = path.match(localeAndTab);
    const articleIdResult = path.match(articleId);

    const locale = localeAndTabResult?.groups?.locale ?? "en";
    const tabFromPath =
        // The new fullscreen Brawl Stars calls the esport tab "esports",
        // so this is a workaround to handle that.
        localeAndTabResult?.groups?.tab === "esports"
            ? "esport"
            : (localeAndTabResult?.groups?.tab ?? "news");

    const tab = tabs.includes(tabFromPath as TabType)
        ? (tabFromPath as TabType)
        : "news";
    let id = ""; // "" means frontpage of news/community/esports

    if (articleIdResult) {
        id = articleIdResult?.groups?.articleId ?? "";
    }

    return {
        locale,
        tab,
        id,
    };
};

const isSamePageView = (a: PageView, b?: PageView) => {
    if (!b) return false;
    return (
        a.page.id === b.page.id &&
        a.page.locale === b.page.locale &&
        a.page.tab === b.page.tab &&
        a.title === b.title
    );
};

const noop = () => {};

// Cap on events held while waiting for the avatar id, so a session that never
// gets one can't grow the queue unbounded. Matches MAX_QUEUE in
// static-utils/src/analytics-worker.ts.
const MAX_PENDING_EVENTS = 100;

type TrackLinkClickParams = {
    url: string;
    id?: string;
    label?: string;
    additionalInformation?: Record<string, unknown>;
};

const SnowplowContext = React.createContext<{
    trackButtonClick: (
        id: string,
        label?: string,
        additionalInformation?: Record<string, unknown>,
    ) => void;
    trackImpression: (id: string, label?: string) => void;
    trackLinkClick: (params: TrackLinkClickParams) => void;
    trackPageView: (title: string, path: string) => void;
    setUser: (userId: string | number) => void;
    setMaintenance: (maintenance: boolean) => void;
}>({
    // Default to no op for everything, so we can use the context without provider in scope
    trackButtonClick: noop,
    trackImpression: noop,
    trackLinkClick: noop,
    trackPageView: noop,
    setUser: noop,
    setMaintenance: noop,
});

export const SnowplowContextProvider = ({
    children,
    config,
}: {
    children: JSX.Element | JSX.Element[];
    config: { appId: string | null; collectorUrl: string | null };
}): JSX.Element | null => {
    // Refs rather than state: callers capture these functions in mount-only
    // effects, so they have to read current values, not their first render's.
    const user = React.useRef<string | number>();
    const maintenance = React.useRef(false);
    const lastPageView = React.useRef<PageView>();
    // Events are held until the avatar id is known, then sent in order with
    // user_identification attached. Without this the first page view of a
    // session, fired on mount before the async JWT handshake finishes, is lost.
    const gateOpen = React.useRef(false);
    const pendingEvents = React.useRef<(() => void)[]>([]);

    const isConfigured = !!config.appId && !!config.collectorUrl;

    if (config.appId && config.collectorUrl) {
        init(config.appId, config.collectorUrl);
    }

    const sendPageView = ({ title, page }: PageView) => {
        addContextAndTrackPageView(title, {
            maintenance: maintenance.current,
            ...page,
        });
    };

    const whenAttributed = (send: () => void) => {
        if (gateOpen.current) {
            send();
            return;
        }
        const snapshot = snapshotPage();
        pendingEvents.current.push(() => trackAsOf(snapshot, send));
        if (pendingEvents.current.length > MAX_PENDING_EVENTS) {
            pendingEvents.current.shift();
        }
    };

    const queued =
        <Args extends unknown[]>(track: (...args: Args) => void) =>
        (...args: Args) =>
            whenAttributed(() => track(...args));

    const openGate = () => {
        if (gateOpen.current) return;
        gateOpen.current = true;
        const pending = pendingEvents.current;
        pendingEvents.current = [];
        pending.forEach((send) => send());
    };

    const trackImpression = (id: string, label?: string) => {
        trackSelfDescribingEvent({
            event: {
                schema: "iglu:com.supercell/impression/jsonschema/1-0-1",
                data: {
                    component_id: id,
                    // impression (version 1-0-1) schema has a 40 character limit on component_name
                    component_name: label?.substring(0, 40) ?? "",
                },
            },
        });
    };

    const trackPageView = (title: string, path: string) => {
        const pageView = { title, page: getPageFromPath(path) };

        if (isSamePageView(pageView, lastPageView.current)) {
            return;
        }
        lastPageView.current = pageView;

        whenAttributed(() => sendPageView(pageView));
    };

    // There is no user id in maintenance mode, so don't wait for one.
    const setMaintenance = (isMaintenance: boolean) => {
        maintenance.current = isMaintenance;
        if (isMaintenance) openGate();
    };

    function setUser(userId: string | number) {
        if (user.current) return;
        user.current = userId;
        addGlobalContexts([
            {
                schema: "iglu:com.supercell/user_identification/jsonschema/1-0-0",
                data: {
                    user_id_type: "avatar_id",
                    user_id: String(userId),
                },
            },
        ]);
        // After addGlobalContexts, so the flushed events carry the user id.
        openGate();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ifConfigured = (fn: any) => (isConfigured ? fn : noop);

    return (
        <SnowplowContext.Provider
            value={{
                trackButtonClick: ifConfigured(queued(trackButtonClick)),
                trackImpression: ifConfigured(queued(trackImpression)),
                trackLinkClick: ifConfigured(queued(trackLinkClick)),
                trackPageView: ifConfigured(trackPageView),
                setUser: ifConfigured(setUser),
                setMaintenance,
            }}
        >
            {children}
        </SnowplowContext.Provider>
    );
};

export const useSnowplowContext = () => React.useContext(SnowplowContext);
